/**
 * AIU Directory Tab
 *
 * Displays all AIU staff members from Azure AD/Entra ID directory.
 * Read-only view with search and pagination.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Alert, Button, Spinner } from 'flowbite-react';
import { HiMail, HiPhone, HiRefresh, HiOfficeBuilding, HiUsers } from 'react-icons/hi';
import { DataTable, type ColumnDef } from '../tables';
import { EmptyState } from '../common/EmptyState';
import { azureAdApi } from '../../api/azureAd';
import type { AzureADUser } from '../../api/azureAd';

export const AIUDirectoryTab: React.FC = () => {
  const [users, setUsers] = useState<AzureADUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Format number with thousands separator
  const formatNumber = (num: number): string => {
    return num.toLocaleString('en-US');
  };

  // Fetch users from API
  const loadUsers = useCallback(async () => {
    try {
      setError(null);
      const response = await azureAdApi.getAIUUsers({
        pageSize: 10000 // Get ALL users (backend returns everything via pagination)
      });

      setUsers(response.users);
      setTotal(response.total);
    } catch (err: any) {
      console.error('Failed to load AIU directory:', err);
      setError(err.message || 'Failed to load AIU directory. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Load users on mount
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Handle manual refresh (clears backend cache)
  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      setError(null);
      await azureAdApi.clearCache();
      await loadUsers();
      setSuccessMessage('Directory refreshed successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to refresh directory');
      setRefreshing(false);
    }
  };

  // Define table columns
  const columns: ColumnDef<AzureADUser>[] = useMemo(() => [
    {
      id: 'displayName',
      header: 'Name',
      accessor: 'displayName',
      sortable: true,
      searchable: true,
      render: (value) => (
        <div className="font-medium text-gray-900 dark:text-white">{value}</div>
      )
    },
    {
      id: 'mail',
      header: 'Email',
      accessor: 'mail',
      sortable: true,
      searchable: true,
      render: (value) => (
        value ? (
          <a
            href={`mailto:${value}`}
            className="flex items-center text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            onClick={(e) => e.stopPropagation()}
          >
            <HiMail className="mr-1 h-4 w-4 flex-shrink-0" />
            <span className="truncate">{value}</span>
          </a>
        ) : (
          <span className="text-sm text-gray-400 italic">Not available</span>
        )
      )
    },
    {
      id: 'jobTitle',
      header: 'Job Title',
      accessor: 'jobTitle',
      sortable: true,
      searchable: true,
      render: (value) => (
        <div className="text-sm text-gray-700 dark:text-gray-300">
          {value || <span className="italic text-gray-400">Not specified</span>}
        </div>
      )
    },
    {
      id: 'department',
      header: 'Department',
      accessor: 'department',
      sortable: true,
      searchable: true,
      render: (value) => (
        value ? (
          <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
            <HiUsers className="mr-1 h-4 w-4 text-gray-400 dark:text-gray-500" />
            {value}
          </div>
        ) : (
          <span className="text-sm text-gray-400 italic">Not specified</span>
        )
      )
    },
    {
      id: 'phone',
      header: 'Phone',
      accessor: 'phone',
      sortable: false,
      render: (value) => (
        value ? (
          <a
            href={`tel:${value}`}
            className="flex items-center text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            onClick={(e) => e.stopPropagation()}
          >
            <HiPhone className="mr-1 h-4 w-4 flex-shrink-0" />
            <span className="truncate">{value}</span>
          </a>
        ) : (
          <span className="text-sm text-gray-400 italic">Not available</span>
        )
      )
    },
    {
      id: 'officeLocation',
      header: 'Office Location',
      accessor: 'officeLocation',
      sortable: true,
      render: (value) => (
        value ? (
          <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
            <HiOfficeBuilding className="mr-1 h-4 w-4 text-gray-400 dark:text-gray-500" />
            {value}
          </div>
        ) : (
          <span className="text-sm text-gray-400 italic">Not specified</span>
        )
      )
    }
  ], []);

  // Loading state
  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Spinner size="xl" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading AIU Internal Directory...</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!loading && users.length === 0 && !error) {
    return (
      <div className="p-6">
        <EmptyState
          title="No AIU staff found"
          description="Unable to retrieve AIU staff from the internal directory. This could be a temporary issue."
          action={{
            label: 'Try Again',
            onClick: handleRefresh,
            icon: <HiRefresh className="mr-2 h-4 w-4" />
          }}
        />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Error Alert */}
      {error && (
        <Alert color="failure" onDismiss={() => setError(null)} className="mb-4">
          <span className="font-medium">Error loading directory:</span> {error}
        </Alert>
      )}

      {/* Success Alert */}
      {successMessage && (
        <Alert color="success" onDismiss={() => setSuccessMessage(null)} className="mb-4">
          {successMessage}
        </Alert>
      )}

      {/* Header with refresh button */}
      <div className="mb-4 flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Full AIU Directory
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Showing {formatNumber(users.length)} of {formatNumber(total)} staff members from AIU Internal Directory
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Data is cached for 30 minutes. Click refresh for latest updates.
          </p>
        </div>
        <Button
          size="sm"
          color="light"
          onClick={handleRefresh}
          disabled={refreshing}
          className="text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 dark:text-blue-400 dark:hover:text-blue-300 dark:bg-blue-900/30 dark:hover:bg-blue-900/50"
        >
          {refreshing ? (
            <>
              <Spinner size="sm" className="mr-2" />
              Refreshing...
            </>
          ) : (
            <>
              <HiRefresh className="mr-2 h-4 w-4" />
              Refresh Directory
            </>
          )}
        </Button>
      </div>

      {/* Directory Table */}
      <DataTable
        data={users}
        columns={columns}
        searchable={true}
        searchPlaceholder="Search by name, email, title, or department..."
        sortable={true}
        pagination={true}
        pageSize={25}
        pageSizeOptions={[10, 25, 50, 100]}
        emptyMessage="No staff members found matching your search."
      />

      {/* Footer info */}
      <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-800">
        <div className="flex items-start">
          <HiUsers className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-900 dark:text-blue-300">
            <p className="font-medium">About this directory</p>
            <p className="mt-1 text-blue-700 dark:text-blue-400">
              This directory displays all AIU staff members from the Azure AD/Entra ID tenant.
              Information is pulled directly from the AIU identity directory and updated automatically.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
